import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Table, Card, Button, Space, Input, Select, Tag, Modal, Progress, List, Badge } from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  PlayCircleOutlined, 
  FileImageOutlined, 
  VideoCameraOutlined,
  CompressOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { getAllTools } from '../../graphql/queries';
import { updateTool, getSignedUrl, getObjectUrl } from '../../graphql/mutation';
import { useNavigate } from 'react-router-dom';
import { getRowLinkProps, withRowLink } from '../../utils/rowLink';
import { compressVideoFromUrl, isFFmpegSupported, getCompressionStatus } from '../../utils/videoCompression';
import { uploadToR2 } from '../../utils/uploadToR2';
import { showSnackBar } from '../../components/SnackBar/SnackBar';
import './Studios.css';

const { Option } = Select;

const TABLE_STATE_KEY = 'toolsTableState';

const readSavedTableState = () => {
  try {
    return JSON.parse(sessionStorage.getItem(TABLE_STATE_KEY)) || {};
  } catch {
    return {};
  }
};

const Tools = () => {
  const navigate = useNavigate();
  const [savedState] = useState(readSavedTableState);
  const [pagination, setPagination] = useState(savedState.pagination || {
    current: 1,
    pageSize: 10,
  });
  const [filters, setFilters] = useState(savedState.filters || {
    isFeatured: null,
    isHidden: false,
    templateId: null,
    name: '',
  });
  const [sortByVideoCount, setSortByVideoCount] = useState(savedState.sortByVideoCount ?? null);
  const pendingScrollY = useRef(savedState.scrollY);
  
  // Bulk generation states
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isBulkModalVisible, setIsBulkModalVisible] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [currentProcessingIndex, setCurrentProcessingIndex] = useState(0);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [processResults, setProcessResults] = useState([]);

  const [updateToolMut] = useMutation(updateTool);
  const [getSignedUrlMut] = useMutation(getSignedUrl);
  const [getObjectUrlMut] = useMutation(getObjectUrl);

  const { loading, data, refetch } = useQuery(getAllTools, {
    variables: {
      filter: {
        isFeatured: filters.isFeatured,
        isHidden: filters.isHidden,
        templateId: filters.templateId || null,
        name: filters.name || null,
      },
      page: pagination.current - 1,
      pageSize: pagination.pageSize,
      sortByVideoCount: sortByVideoCount,
    },
  });

  // Persist table state so returning from a tool detail keeps page, filters and sorting
  useEffect(() => {
    try {
      sessionStorage.setItem(TABLE_STATE_KEY, JSON.stringify({
        pagination,
        filters,
        sortByVideoCount,
      }));
    } catch {}
  }, [pagination, filters, sortByVideoCount]);

  // Restore scroll position once the saved page has loaded
  useEffect(() => {
    if (pendingScrollY.current == null || loading || !data) return;
    const y = pendingScrollY.current;
    pendingScrollY.current = null;
    requestAnimationFrame(() => window.scrollTo(0, y));
  }, [loading, data]);

  const handleRowClick = (record) => {
    try {
      sessionStorage.setItem(TABLE_STATE_KEY, JSON.stringify({
        pagination,
        filters,
        sortByVideoCount,
        scrollY: window.scrollY,
      }));
    } catch {}
    navigate(`/tools/${record.id}`);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    // Reset to first page when filters change
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Get tools that need low resolution processing
  const getToolsNeedingLowRes = () => {
    const allTools = data?.getAllTools?.tools || [];
    return allTools.filter(tool => tool.videoUrl && !tool.lowResolutionUrl);
  };

  // Get selected tools that need processing
  const getSelectedToolsForProcessing = () => {
    const allTools = data?.getAllTools?.tools || [];
    return allTools.filter(tool => 
      selectedRowKeys.includes(tool.id) && 
      tool.videoUrl && 
      !tool.lowResolutionUrl
    );
  };

  // Process a single tool
  const processToolLowRes = async (tool) => {
    try {
      // Compress video from URL
      const lowResFile = await compressVideoFromUrl(tool.videoUrl, setCompressionProgress);

      // Get signed URL for upload
      const { data: signedUrlData } = await getSignedUrlMut({
        variables: { fileName: lowResFile.name }
      });

      // Upload to R2
      await uploadToR2(lowResFile, signedUrlData.getSignedUrl.url, signedUrlData.getSignedUrl.contentType);

      // Get permanent URL
      const { data: objectUrlData } = await getObjectUrlMut({
        variables: { key: signedUrlData.getSignedUrl.id, eTag: 'etag' }
      });

      // Update tool with low resolution URL
      await updateToolMut({
        variables: {
          input: { lowResolutionUrl: objectUrlData.getObjectUrl },
          updateToolId: tool.id
        }
      });

      return { success: true, tool, url: objectUrlData.getObjectUrl };
    } catch (error) {
      console.error(`Failed to process tool ${tool.name}:`, error);
      return { success: false, tool, error: error.message };
    }
  };

  // Start bulk processing
  const handleBulkGenerate = async () => {
    const toolsToProcess = getSelectedToolsForProcessing();
    
    if (toolsToProcess.length === 0) {
      showSnackBar('warning', 'No Tools', 'No selected tools need low resolution processing');
      return;
    }

    setBulkProcessing(true);
    setProcessResults([]);
    setCurrentProcessingIndex(0);

    const results = [];
    
    for (let i = 0; i < toolsToProcess.length; i++) {
      setCurrentProcessingIndex(i);
      setCompressionProgress(0);
      
      const result = await processToolLowRes(toolsToProcess[i]);
      results.push(result);
      setProcessResults([...results]);
    }

    setBulkProcessing(false);
    setSelectedRowKeys([]);
    refetch();
    
    const successCount = results.filter(r => r.success).length;
    showSnackBar(
      successCount === results.length ? 'success' : 'warning',
      'Bulk Processing Complete',
      `${successCount}/${results.length} tools processed successfully`
    );
  };

  // Open bulk modal
  const openBulkModal = () => {
    setProcessResults([]);
    setIsBulkModalVisible(true);
  };

  // Row selection config
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({
      disabled: !record.videoUrl, // Disable if no video
    }),
  };

  const columns = [
    {
      title: 'Banner',
      dataIndex: 'bannerUrl',
      key: 'bannerUrl',
      width: '100px',
      render: (bannerUrl) => bannerUrl ? (
        <img 
          src={bannerUrl} 
          alt="Banner" 
          className="studio-banner"
        />
      ) : '-',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '100px',
    },
    {
      title: 'Type',
      dataIndex: 'imageType',
      key: 'imageType',
      width: '100px',
      render: (imageType) => (
        <Tag color={imageType === 'DUAL' ? 'blue' : 'green'}>
          {imageType}
        </Tag>
      ),
    },
    {
      title: 'Featured',
      dataIndex: 'isFeatured',
      key: 'isFeatured',
      width: '100px',
      render: (isFeatured) => (
        <Tag color={isFeatured ? 'gold' : 'default'}>
          {isFeatured ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Hidden',
      dataIndex: 'isHidden',
      key: 'isHidden',
      width: '100px',
      render: (isHidden) => (
        <Tag color={isHidden ? 'red' : 'default'}>
          {isHidden ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      width: '120px',
      render: (provider) => provider ? (
        <Tag color="blue">
          {provider}
        </Tag>
      ) : '-',
    },

    {
      title: 'Credit Cost',
      dataIndex: 'cost',
      key: 'cost',
      width: '80px',
      render: (cost) => cost ? `${cost}` : '-',
    },
    {
      title: 'Dollar Cost',
      dataIndex: 'actualCost',
      key: 'actualCost',
      width: '80px',
      render: (actualCost) => actualCost ? `$${actualCost}` : '-',
    },
    {
      title: 'Videos',
      dataIndex: 'videoCount',
      key: 'videoCount',
      width: '25px',
      sorter: true,
      sortOrder: sortByVideoCount === 'DESC' ? 'ascend' : 
                 sortByVideoCount === 'ASC' ? 'descend' : null,
    },
    {
      title: 'Low Res',
      key: 'lowRes',
      width: '80px',
      render: (_, record) => {
        if (!record.videoUrl) {
          return <Tag>No Video</Tag>;
        }
        return record.lowResolutionUrl ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>Yes</Tag>
        ) : (
          <Tag color="warning" icon={<ExclamationCircleOutlined />}>Missing</Tag>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: '120px',
      render: (date) => new Date(date).toLocaleDateString(),
    },
  ];

  const handleTableChange = (paginationConfig, filters, sorter) => {
    // Handle sorting for videoCount column
    let nextSort = sortByVideoCount;
    if (sorter.field === 'videoCount') {
      if (sorter.order === 'ascend') {
        nextSort = 'DESC'; // First click shows big numbers
      } else if (sorter.order === 'descend') {
        nextSort = 'ASC'; // Second click shows small numbers
      } else {
        nextSort = null;
      }
    } else if (!sorter.field) {
      // Reset sorting when no sort is applied
      nextSort = null;
    }
    setSortByVideoCount(nextSort);

    setPagination({
      // Reset to first page when sorting changes
      current: nextSort !== sortByVideoCount ? 1 : paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    });
  };

  return (
    <div className="studios-container">
      <Card>
        <div className="page-header">
          <div className="header-content">
            <h1>Tools Management</h1>
            <p>Manage and organize your video generation tools</p>
          </div>
          <Space>
            {isFFmpegSupported() && (
              <Button 
                size="large"
                icon={<CompressOutlined />}
                onClick={openBulkModal}
                disabled={selectedRowKeys.length === 0}
              >
                Bulk Generate Low Res {selectedRowKeys.length > 0 && `(${selectedRowKeys.length})`}
              </Button>
            )}
            <Button 
              type="primary" 
              size="large"
              icon={<PlusOutlined />}
              onClick={() => navigate('/tools/create')}
            >
              Create New Tool
            </Button>
          </Space>
        </div>

        <div className="filters-section">
          <Space size="large" wrap>
            <div className="filter-item">
              <Input
                placeholder="Search tools by name..."
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
                style={{ width: 250 }}
                prefix={<SearchOutlined />}
                allowClear
              />
            </div>
            <div className="filter-item">
              <Select
                placeholder="Featured"
                value={filters.isFeatured}
                onChange={(value) => handleFilterChange('isFeatured', value)}
                style={{ width: 120 }}
                allowClear
              >
                <Option value={true}>Featured</Option>
                <Option value={false}>Regular</Option>
              </Select>
            </div>
            <div className="filter-item">
              <Select
                placeholder="Visibility"
                value={filters.isHidden}
                onChange={(value) => handleFilterChange('isHidden', value)}
                style={{ width: 120 }}
                allowClear
              >
                <Option value={true}>Hidden</Option>
                <Option value={false}>Visible</Option>
              </Select>
            </div>
            <div className="filter-item">
              <Input
                placeholder="Template ID"
                value={filters.templateId}
                onChange={(e) => handleFilterChange('templateId', e.target.value)}
                style={{ width: 130 }}
                allowClear
              />
            </div>
          </Space>
        </div>
        
        {/* Info bar showing tools needing low res */}
        {isFFmpegSupported() && getToolsNeedingLowRes().length > 0 && (
          <div style={{ 
            marginBottom: 16, 
            padding: '8px 16px', 
            background: '#fff7e6', 
            border: '1px solid #ffd591',
            borderRadius: 6,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>
              <ExclamationCircleOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
              <strong>{getToolsNeedingLowRes().length}</strong> tools on this page have videos but are missing low resolution versions
            </span>
            <Button 
              size="small" 
              onClick={() => {
                const toolIds = getToolsNeedingLowRes().map(t => t.id);
                setSelectedRowKeys(toolIds);
              }}
            >
              Select All Missing
            </Button>
          </div>
        )}

        <Table
          columns={withRowLink(columns, (record) => `/tools/${record.id}`)}
          dataSource={data?.getAllTools?.tools || []}
          loading={loading}
          rowKey="id"
          rowSelection={isFFmpegSupported() ? rowSelection : undefined}
          pagination={{
            ...pagination,
            total: data?.getAllTools?.count || 0,
            showSizeChanger: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} tools`,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            // Don't navigate if clicking on checkbox
            ...getRowLinkProps(`/tools/${record.id}`, () => handleRowClick(record), {
              shouldIgnore: (e) => e.target.type === 'checkbox',
            }),
          })}
        />
      </Card>

      {/* Bulk Processing Modal */}
      <Modal
        title="Bulk Generate Low Resolution Videos"
        open={isBulkModalVisible}
        onCancel={() => !bulkProcessing && setIsBulkModalVisible(false)}
        footer={[
          <Button 
            key="cancel" 
            onClick={() => setIsBulkModalVisible(false)}
            disabled={bulkProcessing}
          >
            {bulkProcessing ? 'Processing...' : 'Close'}
          </Button>,
          !bulkProcessing && processResults.length === 0 && (
            <Button 
              key="start" 
              type="primary" 
              onClick={handleBulkGenerate}
              disabled={getSelectedToolsForProcessing().length === 0}
            >
              Start Processing ({getSelectedToolsForProcessing().length} tools)
            </Button>
          ),
        ]}
        closable={!bulkProcessing}
        maskClosable={!bulkProcessing}
        width={600}
      >
        {/* Pre-processing view */}
        {!bulkProcessing && processResults.length === 0 && (
          <div>
            <p style={{ marginBottom: 16 }}>
              The following <strong>{getSelectedToolsForProcessing().length}</strong> tools will be processed:
            </p>
            <List
              size="small"
              bordered
              dataSource={getSelectedToolsForProcessing()}
              style={{ maxHeight: 300, overflow: 'auto' }}
              renderItem={(tool) => (
                <List.Item>
                  <Space>
                    {tool.bannerUrl && (
                      <img 
                        src={tool.bannerUrl} 
                        alt="" 
                        style={{ width: 40, height: 24, objectFit: 'cover', borderRadius: 4 }} 
                      />
                    )}
                    <span>{tool.name}</span>
                  </Space>
                </List.Item>
              )}
            />
            {getSelectedToolsForProcessing().length === 0 && (
              <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>
                No selected tools need low resolution processing.
                <br />
                Select tools that have videos but are missing low resolution versions.
              </div>
            )}
          </div>
        )}

        {/* Processing view */}
        {bulkProcessing && (
          <div>
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <LoadingOutlined style={{ fontSize: 24, marginBottom: 8 }} />
              <p>
                Processing tool {currentProcessingIndex + 1} of {getSelectedToolsForProcessing().length}:
                <br />
                <strong>{getSelectedToolsForProcessing()[currentProcessingIndex]?.name}</strong>
              </p>
              <Progress 
                percent={compressionProgress} 
                status="active"
                format={() => getCompressionStatus(compressionProgress)}
              />
            </div>
            
            {/* Results so far */}
            {processResults.length > 0 && (
              <List
                size="small"
                bordered
                dataSource={processResults}
                style={{ maxHeight: 200, overflow: 'auto' }}
                renderItem={(result) => (
                  <List.Item>
                    <Space>
                      {result.success ? (
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      ) : (
                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                      )}
                      <span>{result.tool.name}</span>
                      {!result.success && (
                        <Tag color="error">{result.error}</Tag>
                      )}
                    </Space>
                  </List.Item>
                )}
              />
            )}
          </div>
        )}

        {/* Completed view */}
        {!bulkProcessing && processResults.length > 0 && (
          <div>
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 8 }} />
              <p>
                <strong>{processResults.filter(r => r.success).length}</strong> of{' '}
                <strong>{processResults.length}</strong> tools processed successfully
              </p>
            </div>
            <List
              size="small"
              bordered
              dataSource={processResults}
              style={{ maxHeight: 300, overflow: 'auto' }}
              renderItem={(result) => (
                <List.Item>
                  <Space>
                    {result.success ? (
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                    )}
                    <span>{result.tool.name}</span>
                    {result.success ? (
                      <Tag color="success">Complete</Tag>
                    ) : (
                      <Tag color="error">{result.error}</Tag>
                    )}
                  </Space>
                </List.Item>
              )}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Tools; 